// Phase 11 — small "+" button that adds a symbol to the clipboard tray.

import { useSyncExternalStore } from "react";
import { addToTray, removeFromTray, useTray } from "@/lib/clipboard-tray";

interface Props {
  symbol: string;
  /** Visual variant — "icon" for grid overlays, "chip" for inline rows. */
  variant?: "icon" | "chip";
  /** Optional label override for the chip variant. */
  label?: string;
}

export function CollectButton({ symbol, variant = "icon", label }: Props) {
  const tray = useTray();
  const inTray = tray.items.includes(symbol);

  const toggle = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (inTray) removeFromTray(symbol);
    else addToTray(symbol);
  };

  if (variant === "chip") {
    return (
      <button
        type="button"
        onClick={toggle}
        aria-pressed={inTray}
        aria-label={inTray ? `Remove ${symbol} from tray` : `Add ${symbol} to tray`}
        className={
          "inline-flex items-center justify-center rounded-md border px-3 py-2 text-sm font-medium transition-colors " +
          (inTray
            ? "border-accent bg-accent/15 text-accent hover:bg-accent/20"
            : "border-border bg-surface/60 text-foreground hover:bg-secondary")
        }
      >
        <span aria-hidden="true" className="mr-1.5">{inTray ? "✓" : "+"}</span>
        {label ?? (inTray ? "In tray" : "Add to tray")}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={toggle}
      aria-pressed={inTray}
      aria-label={inTray ? `Remove ${symbol} from tray` : `Add ${symbol} to tray`}
      title={inTray ? "Remove from tray" : "Add to tray"}
      className={
        "collect-button absolute right-1 top-1 z-10 inline-flex h-6 w-6 items-center justify-center rounded-md border text-[12px] font-bold leading-none transition " +
        (inTray
          ? "border-accent bg-accent/20 text-accent opacity-100"
          : // Hover devices: reveal on hover/focus to keep tiles clean. Touch
            // devices have no hover (Tailwind gates group-hover behind
            // @media(hover:hover)), so force it visible there or it's unusable.
            "border-border bg-surface/80 text-muted-foreground opacity-0 group-hover:opacity-100 focus-visible:opacity-100 [@media(hover:none)]:opacity-100")
      }
    >
      <span aria-hidden="true">{inTray ? "✓" : "+"}</span>
    </button>
  );
}

// SSR-safe wrapper for grids — avoids hydration mismatch when tray state is
// non-empty client-side but always empty during SSR.
export function useIsClient(): boolean {
  return useSyncExternalStore(
    (cb) => {
      // No subscription needed — value is stable.
      cb();
      return () => {};
    },
    () => true,
    () => false,
  );
}
